import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
    Box, Button, Checkbox, Input, Radio, RadioGroup, Stack, Text, Center,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon
} from "@chakra-ui/react"

import { addLayer, editLayer } from "../../features/creator/creatorSlice"
import StationSearch from "../StationSearch"
import ViaPointSubform from "../ViaPointSubform"
import FormElement from '../ui/FormElement'
import FormHeading from '../ui/FormHeading'
import { generateNameForLayer } from "../../utilities/creatorHelper"

export default function LayerForm({ mapBeingEdited, editPrefilledLayer, editModalOnClose }) {

    const { isLoading, isSuccessManipulating } = useSelector((state) => state.creator)

    const dispatch = useDispatch()

    const [isAddingType, setIsAddingType] = useState('line')
    const [markerSelectedOption, setMarkerSelectedOption] = useState('station')

    const [destinationStation, setDestinationStation] = useState(undefined)

    const [disableDescriptionGeneration, setDisableDescriptionGeneration] = useState(false)

    const formik = useFormik({
        initialValues: {
            description: '',

            // Line
            origin: undefined,
            destination: undefined,
            viaPointsIds: [],
            lineMarkerAtStart: true,
            lineMarkerAtEnd: true,
            lineStyling: {
                color: '#000000',
                variant: 'solid',
                opacity: 1,
            },
            markerStyling: {
                variant: 'solid',
            },

            // Marker
            markerStation: undefined,
            lat: undefined,
            lon: undefined,
        },
        onSubmit: (values) => {
            if (editPrefilledLayer === undefined) { // New layer
                if (isAddingType === 'line') {
                    dispatch(addLayer({
                        mapId: mapBeingEdited._id,
                        type: 'line',
                        description: values.description,
                        layerData: {
                            originId: values.origin._id,
                            destinationId: values.destination._id,
                            viaPointsIds: values.viaPointsIds,
                            lineMarkerAtStart: values.lineMarkerAtStart,
                            lineMarkerAtEnd: values.lineMarkerAtEnd,
                            lineStyling: values.lineStyling,
                        },
                    }))

                    formik.setFieldValue('destination', undefined)
                    formik.setFieldValue('origin', destinationStation)
                }

                if (isAddingType === 'marker') {

                    dispatch(addLayer({
                        mapId: mapBeingEdited._id,
                        type: 'marker',
                        description: values.description,
                        layerData: {
                            lat: values.markerStation ? values.markerStation.latitude : values.lat,
                            lon: values.markerStation ? values.markerStation.longitude : values.lon,
                            markerStyling: values.markerStyling,
                        },
                    }))

                    formik.setFieldValue('markerStation', undefined)
                    formik.setFieldValue('lat', undefined)
                    formik.setFieldValue('lon', undefined)
                }

                formik.setFieldValue('description', '')
            } else {
                // Editing layer
                if (isAddingType === 'line') {
                    dispatch(editLayer({
                        mapId: mapBeingEdited._id,
                        layerId: editPrefilledLayer._id,
                        description: values.description,
                        layerData: {
                            lineMarkerAtStart: values.lineMarkerAtStart,
                            lineMarkerAtEnd: values.lineMarkerAtEnd,
                            lineStyling: values.lineStyling,
                        },
                    }))
                }

                if (isAddingType === 'marker') {
                    dispatch(editLayer({
                        mapId: mapBeingEdited._id,
                        layerId: editPrefilledLayer._id,
                        description: values.description,
                        layerData: {
                            markerStyling: values.markerStyling,
                        },
                    }))
                }

                editModalOnClose()
                formik.resetForm()
            }

            setDisableDescriptionGeneration(false)
        }
    })

    useEffect(() => {
        if (editPrefilledLayer !== undefined) {
            setDisableDescriptionGeneration(true)

            formik.setFieldValue('description', editPrefilledLayer.description)

            if (editPrefilledLayer.line) {
                setIsAddingType('line')
                
                formik.setFieldValue('lineMarkerAtStart', editPrefilledLayer.line.startMarker ? true : false)
                formik.setFieldValue('lineMarkerAtEnd', editPrefilledLayer.line.endMarker ? true : false)
                formik.setFieldValue('lineStyling', editPrefilledLayer.line)
            }

            if (editPrefilledLayer.marker) {
                setIsAddingType('marker')

                formik.setFieldValue('markerStation', editPrefilledLayer.marker.position)
                formik.setFieldValue('markerStyling', editPrefilledLayer.marker)
            }
        }
    }, [editPrefilledLayer])

    // Description field generation
    function descriptionFieldOnChange(e) {
        formik.handleChange(e)

        if (!disableDescriptionGeneration)
            setDisableDescriptionGeneration(true)
    }
    
        useEffect(() => {
            const generatedName = generateNameForLayer(formik.values, isAddingType)
    
            if (!disableDescriptionGeneration && generatedName !== formik.values.description)
                formik.setFieldValue('description', generatedName)
        }, [formik.values])

    return (
        <Box>
            <form onSubmit={formik.handleSubmit}>
                {!editPrefilledLayer && (
                    <FormElement>
                        <FormHeading>Type of layer:</FormHeading>
                        <RadioGroup onChange={setIsAddingType} value={isAddingType}>
                            <Stack direction='row' spacing={4}>
                                <Radio value='line' size="lg" colorScheme="blue">
                                    Line
                                </Radio>
                                <Radio value='marker' size="lg" colorScheme="green">
                                    Marker
                                </Radio>
                            </Stack>
                        </RadioGroup>
                    </FormElement>
                )}

                {isAddingType === 'line' && (
                    <>
                        {!editPrefilledLayer && (
                            <FormElement>
                                <FormHeading>Origin Station:</FormHeading>
                                <StationSearch
                                    setSelectedStation={(station) => {
                                        station && (formik.setFieldValue('origin', station))
                                    }}
                                    prefilledText={(destinationStation !== undefined) ? destinationStation.name : ''}
                                />
                            </FormElement>
                        )}
                        <FormElement>
                            <Stack direction="row">
                                <Text>Marker at start</Text>
                                <input
                                    type="checkbox"
                                    id="lineMarkerAtStart"
                                    name="lineMarkerAtStart"
                                    checked={formik.values.lineMarkerAtStart}
                                    onChange={(e) => formik.setFieldValue('lineMarkerAtStart', e.target.checked)}
                                />
                            </Stack>
                        </FormElement>

                        {!editPrefilledLayer && (
                            <>
                                <FormElement>
                                    <ViaPointSubform
                                        setViaPointsIds={(idsArray) => formik.setFieldValue('viaPointsIds', idsArray)}
                                    />
                                </FormElement>

                                <FormElement>
                                    <FormHeading>Destination Station:</FormHeading>
                                    <StationSearch
                                        setSelectedStation={(station) => {
                                            if (station !== undefined) {
                                                station && formik.setFieldValue('destination', station);
                                                setDestinationStation(station);
                                            }
                                        }}
                                        prefilledText={''}
                                    />
                                </FormElement>
                            </>
                        )}

                        <FormElement>
                            <Stack direction="row">
                                <Text>Marker at end</Text>
                                <input
                                    type="checkbox"
                                    id="lineMarkerAtEnd"
                                    name="lineMarkerAtEnd"
                                    checked={formik.values.lineMarkerAtEnd}
                                    onChange={(e) => formik.setFieldValue('lineMarkerAtEnd', e.target.checked)}
                                />
                            </Stack>
                        </FormElement>

                        <Accordion
                            allowMultiple
                            defaultIndex={editPrefilledLayer ? [0] : undefined} // Expanded by default on edit
                        >
                            <AccordionItem>
                                <h2>
                                    <AccordionButton>
                                        <Box flex="1" textAlign="left">
                                            Line styling
                                        </Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4}>
                                    <Stack>
                                        <Box>
                                            <Text>Line color (RGB):</Text>
                                            <Input
                                                placeholder="#000000"
                                                id="lineStyling.color"
                                                name="lineStyling.color"
                                                value={formik.values.lineStyling.color}
                                                onChange={formik.handleChange}
                                            />
                                        </Box>
                                        <Box>
                                            <Text>Line type:</Text>
                                            <div onChange={(e) => formik.setFieldValue("lineStyling.variant", e.target.value)}>
                                                <input
                                                    type="radio"
                                                    id="solid"
                                                    name="lineStyling.variant"
                                                    value="solid"
                                                    checked={formik.values.lineStyling.variant === "solid"}
                                                />
                                                <label htmlFor="solid">Solid</label>

                                                <input
                                                    type="radio"
                                                    id="dashed"
                                                    name="lineStyling.variant"
                                                    value="dashed"
                                                    checked={formik.values.lineStyling.variant === "dashed"}
                                                />
                                                <label htmlFor="dashed">Dashed</label>
                                            </div>
                                        </Box>
                                        <Box>
                                            <Text>Opacity (0-1 decimal):</Text>
                                            <Input
                                                placeholder="0-1 (e.g. 0.75)"
                                                id="lineStyling.opacity"
                                                name="lineStyling.opacity"
                                                value={formik.values.lineStyling.opacity}
                                                onChange={formik.handleChange}
                                            />
                                        </Box>
                                    </Stack>
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                    </>
                )}

                {isAddingType === 'marker' && (
                    <>
                        {!editPrefilledLayer && (
                            <>
                                <FormElement>
                                    <FormHeading>Location:</FormHeading>
                                    <RadioGroup onChange={setMarkerSelectedOption} value={markerSelectedOption}>
                                        <Stack direction="row">
                                            <Radio value="station">Station</Radio>
                                            <Radio value="custom">Coordinates</Radio>
                                        </Stack>
                                    </RadioGroup>
                                </FormElement>

                                {markerSelectedOption === 'station' ? (
                                    <FormElement>
                                        <FormHeading>Station:</FormHeading>
                                        <StationSearch
                                            setSelectedStation={(station) => {
                                                if (station !== undefined) {
                                                    formik.setFieldValue('markerStation', station);
                                                }
                                            }}
                                            prefilledText={''}
                                        />
                                    </FormElement>
                                ) : (
                                    <>
                                        <FormElement>
                                            <FormHeading>Latitude:</FormHeading>
                                            <Input
                                                placeholder="Latitude"
                                                id="lat"
                                                name="lat"
                                                value={formik.values.lat}
                                                onChange={formik.handleChange}
                                            />
                                        </FormElement>

                                        <FormElement>
                                            <FormHeading>Longitude:</FormHeading>
                                            <Input
                                                placeholder="Longitude"
                                                id="lon"
                                                name="lon"
                                                value={formik.values.lon}
                                                onChange={formik.handleChange}
                                            />
                                        </FormElement>
                                    </>
                                )}
                            </>
                        )}

                        <Accordion allowMultiple defaultIndex={editPrefilledLayer ? [0] : undefined}>
                            <AccordionItem>
                                <h2>
                                    <AccordionButton>
                                        <Box flex="1" textAlign="left">
                                            Marker styling
                                        </Box>
                                        <AccordionIcon />
                                    </AccordionButton>
                                </h2>
                                <AccordionPanel pb={4}>
                                    <Stack>
                                        <Box>
                                            <Text>Marker type:</Text>
                                            <div onChange={(e) => formik.setFieldValue("markerStyling.variant", e.target.value)}>
                                                <input
                                                    type="radio"
                                                    id="solid"
                                                    name="markerStyling.variant"
                                                    value="solid"
                                                    checked={formik.values.markerStyling.variant === "solid"}
                                                />
                                                <label htmlFor="solid">Solid</label>

                                                <input
                                                    type="radio"
                                                    id="outline"
                                                    name="markerStyling.variant"
                                                    value="outline"
                                                    checked={formik.values.markerStyling.variant === "outline"}
                                                />
                                                <label htmlFor="outline">Outline</label>
                                            </div>
                                        </Box>
                                    </Stack>
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                    </>
                )}

                <FormElement>
                    <FormHeading>Description:</FormHeading>
                    <Input
                        placeholder="Description"
                        id="description"
                        name="description"
                        value={formik.values.description}
                        onChange={descriptionFieldOnChange}
                        size="lg"
                        as="textarea"
                        minHeight='24'
                    />
                </FormElement>

                <FormElement>
                    <Center>
                        <Button type="submit" isLoading={isLoading}>
                            {!editPrefilledLayer ? 'Add layer' : 'Edit layer'}
                        </Button>
                    </Center>
                </FormElement>
            </form>
        </Box>
    )
}